import React, { useState, useEffect, useRef } from "react";

/**
 * Two-step level, matching the earlier agreed design:
 * 1. Child taps an answer (graded normally).
 * 2. Parent actively confirms the "ask one more why" conversation
 *    happened — this is what makes Level 5 the differentiator: the
 *    reward step requires the parent to participate, not just the child.
 */
export default function Level5ThinkWhy({ questions, sound, onComplete }) {
  const [qIndex, setQIndex] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [stage, setStage] = useState("answer"); // answer | parentPrompt
  const [feedback, setFeedback] = useState(null);
  const startTimeRef = useRef(Date.now());

  const q = questions[qIndex];
  const total = questions.length;

  useEffect(() => { startTimeRef.current = Date.now(); }, []);

  const handleTap = (option) => {
    if (feedback) return;
    const isCorrect = option === q.answer;
    sound[isCorrect ? "correct" : "wrong"]();
    setFeedback({ option, correct: isCorrect });
    if (isCorrect) setCorrectCount((c) => c + 1);

    setTimeout(() => {
      setFeedback(null);
      setStage("parentPrompt");
    }, 700);
  };

  const handleParentConfirm = () => {
    sound.tap();
    if (qIndex + 1 < total) {
      setQIndex((i) => i + 1);
      setStage("answer");
    } else {
      const timeTakenSec = Math.round((Date.now() - startTimeRef.current) / 1000);
      onComplete({ correctCount, totalQuestions: total, timeTakenSec, hintsUsed: 0 });
    }
  };

  if (!q) return null;

  if (stage === "parentPrompt") {
    return (
      <div className="flex flex-col items-center gap-5 py-8 px-4 text-center">
        <div className="text-4xl text-cyan-100" aria-hidden="true">💬</div>
        <p className="text-sm font-bold uppercase tracking-wide text-[#FF8B6B]">For the grown-up</p>
        <p className="text-lg sm:text-xl font-bold text-cyan-100 max-w-xs">{q.parentPrompt}</p>
        <p className="text-xs text-gray-100 max-w-xs">There's no wrong answer — the goal is just talking it through together.</p>
        <button
          onClick={handleParentConfirm}
          className="bg-[#7C3AED] text-white font-bold rounded-2xl px-6 py-3 text-sm sm:text-base"
        >
          We talked about it! ✅
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 py-6 px-4">
      <div className="text-5xl sm:text-6xl" aria-hidden="true">{q.emoji}</div>
      <p className="text-lg sm:text-xl font-bold text-white text-center">{q.prompt}</p>
      <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
        {q.options.map((opt, idx) => {
          const isTapped = feedback?.option === opt;
          const showState = isTapped ? (feedback.correct ? "correct" : "wrong") : null;
          return (
            <button
              key={idx}
              onClick={() => handleTap(opt)}
              disabled={!!feedback}
              className={[
                "rounded-2xl py-4 text-sm sm:text-base font-semibold shadow-sm transition-all",
                showState === "correct" ? "bg-[#DCFCE7] ring-4 ring-[#4ADE80]" :
                showState === "wrong" ? "bg-[#FEE2E2] ring-4 ring-red-300" :
                "bg-gray-800 text-gray-100 active:scale-95",
              ].join(" ")}
            >
              {opt}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-cyan-100">Question {qIndex + 1} of {total}</p>
    </div>
  );
}
